import React, { useState } from 'react'
import { NestAPI } from '@marketing-milk/frontend'
import { toast } from 'react-toastify'

interface Options {
  /** Refetch the sprint creatives after a successful refresh so repaired relations/URLs show. */
  refetchCreatives: () => Promise<unknown> | unknown
  /** Internal staff only — controls whether the auth-failure toast links to the Authorizers page. */
  showAuthorizerLink: boolean
}

// A Facebook token/authorization problem is the expected failure mode here. It
// surfaces on the creatives sync (the ad-videos sync swallows its own errors),
// usually as a 401/403 or a message mentioning the token/OAuth.
function looksLikeAuthFailure(err: any): boolean {
  const status = err?.response?.status
  if (status === 401 || status === 403) return true
  const message = `${err?.response?.data?.message ?? err?.message ?? ''}`.toLowerCase()
  return /token|oauth|authoriz|permission|expired/.test(message)
}

/**
 * Staff-only "Refresh video assets" action. Reuses the existing manual sync
 * endpoints — `ad-videos` first so the creatives sync can attach the freshly
 * synced video rows — then refetches the sprint creatives. Both endpoints still
 * enforce business access on the server; the permission gate is only about who
 * sees the button and the authorizer hint.
 */
export function useRefreshVideoAssets(
  businessID: number,
  { refetchCreatives, showAuthorizerLink }: Options
) {
  const [isRefreshing, setIsRefreshing] = useState(false)

  const refresh = async () => {
    if (isRefreshing) return
    setIsRefreshing(true)
    try {
      await NestAPI.get(`businesses/${businessID}/ad-builder/sync/ad-videos`)
      await NestAPI.get(`businesses/${businessID}/ad-builder/sync/creatives`)
      await refetchCreatives()
      toast.success('Video assets refreshed from Facebook')
    } catch (err) {
      if (looksLikeAuthFailure(err) && showAuthorizerLink) {
        toast.error(
          <span>
            Facebook authorization may need attention.{' '}
            <a
              href={`${process.env.REACT_APP_ADMIN}/business/authorizers/list`}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              Check authorizers ↗
            </a>
          </span>
        )
      } else {
        toast.error('Failed to refresh video assets. Please try again.')
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  return { refresh, isRefreshing }
}
